

import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { IAppState, IFileResource } from "../models/IAppState";
import { IInitAppState } from "../models/IInitAppState";
import IServerURIs from "../models/configuration/servers/IServerURIs";
import { IRequestId } from "../models/IRequestIds";
import { IDeliveryType } from "../models/configuration/plugin/IConfiguration";
import { IDocListItem } from "../models/IDocListResponse";
import { REQUEST_TYPES, REQUEST_TYPE_LABEL } from "../utils/constants";

const initialState = {
    page: '/',
    loadingState: {
        loading: true,
        loadingMessage: '',
    },
    defaultPackageId: '',
    accessToken: null,
    expirationDateTimeUTC: new Date().toISOString(),
    encompassPersonas: [],
    userPersonas: [],
    configId: null,
    configIds: [],
    isAdmin: false,
    isASO: false,
    needSetupRedirect: false,
    needHomeRedirect: false,
    attachments: [],
    loanId: null,
    loanNumber: null,
    borrowerName: null,
    userId: null,
    userFullName: null,
    clientCode: null,
    serverUris: null,
    requestIds: [],
    fieldIdValuePairs : {},
    borrowerPairIds: [],
    selectedPackage: null,
    configurationSave: {
        hasSaveFunction: false,
        isConfigurationPage: false,
    },
    orderState: {
        packageId: '',
        altPackageId: '',
        parentPackageId: '',
        requestType: '',
        selectedDeliveryMethods: [],
        selectedDocumentList: [],
        orderCount: 0,
        requestTypeVisible: false,
        requestTypeEnable: false,
        requestTypeTitle: REQUEST_TYPE_LABEL,
        solexEditorLink: null,
    }
} satisfies IAppState as IAppState;

export const appSlice = createSlice({
    name: 'app',
    initialState,
    reducers: {
        setPage(state, action: PayloadAction<string>) {
            state.page = action.payload;
        },
        setDefaultPackageId(state, action: PayloadAction<string>) {
            state.defaultPackageId = action.payload;
        },
        setLoadingState(state, action: PayloadAction<{ loading: boolean, loadingMessage?: string }>) {
            state.loadingState.loading = action.payload.loading;
            if (action.payload.loadingMessage !== undefined) {
                state.loadingState.loadingMessage = action.payload.loadingMessage;
            }
        },
        setConfigId(state, action: PayloadAction<string>) {
            state.configId = action.payload;
        },
        setConfigIds(state, action: PayloadAction<string[]>) {
            state.configIds = action.payload ?? [];
        },
        setNeedSetupRedirect(state, action: PayloadAction<boolean>) {
            state.needSetupRedirect = action.payload;
        },
        setNeedHomeRedirect(state, action: PayloadAction<boolean>) {
            state.needHomeRedirect = action.payload;
        },
        setIsASO(state, action: PayloadAction<boolean>) {
            state.isASO = action.payload;
        },
        addAttachments(state, action: PayloadAction<Array<IFileResource>>) {
            action.payload.forEach((attachment) => {
                if (!state.attachments.some(a => a.name === attachment.name && a.source === attachment.source))
                {
                    state.attachments.push({...attachment, docType: "ExternalAttachments"});
                }                    
            })
        },
        clearAttachments(state) {
            state.attachments = [];
        },
        removeAttachment(state, action: PayloadAction<string>) {
            state.attachments = state.attachments.filter(item => item.id !== action.payload);
        },

        initAppStateFromOrigin(state, action: PayloadAction<IInitAppState>) {
            state.accessToken = action?.payload?.authToken;
            state.expirationDateTimeUTC = action?.payload?.authExpires;
            state.configId = action?.payload?.configId;
            state.userId = action?.payload?.userId?.substring(0, action?.payload?.userId.indexOf("@")) ?? null;
            state.userFullName = action?.payload?.userFullName ?? null;
            state.encompassPersonas = action?.payload?.encompassPersonas ?? [];
            state.userPersonas = action?.payload?.personas;
            state.loanId = action?.payload?.loanID;
            state.loanNumber = action?.payload?.loanNumber;
            state.borrowerName = action?.payload?.borrowerName;
            // isAdmin is resolved upstream (originService) where TPO Connect context is known,
            // so TPO personas (e.g. "TPO Administrator") are never mistaken for Encompass admins.
            state.isAdmin = action?.payload?.isAdmin ?? false;
            if(action?.payload?.fieldIdValuePairs !== null)
                state.fieldIdValuePairs = action?.payload?.fieldIdValuePairs
            if(action?.payload?.borrowerPairIds !== null) 
                state.borrowerPairIds = action?.payload?.borrowerPairIds            
            
        },
        setAuthToken(state, action: PayloadAction<{ authToken: string | null; authExpires: string | null; }>) {
            state.accessToken = action.payload.authToken;
            state.expirationDateTimeUTC = action.payload.authExpires;
        },
        setClientCode(state, action: PayloadAction<string>) {
            state.clientCode = action.payload;
        },
        setServerUris(state, action: PayloadAction<IServerURIs>) {
            state.serverUris = action.payload;
        },
        addRequestId(state, action: PayloadAction<IRequestId>) {
            state.requestIds.push(action.payload);
            state.requestIds.sort((a, b) => { return (b.Date?.getTime() ?? 0) - (a.Date?.getTime() ?? 0) });
        },
        setSelectedPackage(state, action: PayloadAction<string | null>) {
            state.selectedPackage = action.payload;
        },
        
        // Order state actions
        setOrderPackageId(state, action: PayloadAction<string>) {
            state.orderState.packageId = action.payload;
        },
        setOrderAltPackageId(state, action: PayloadAction<string>) {
            state.orderState.altPackageId = action.payload;
        },
        setOrderParentPackageId(state, action: PayloadAction<string>) {
            state.orderState.parentPackageId = action.payload;
        },
        setOrderRequestType(state, action: PayloadAction<string>) {
            state.orderState.requestType = action.payload;
        },
        setOrderSelectedDeliveryMethods(state, action: PayloadAction<Array<IDeliveryType>>) {
            // Skip update if both current and new values are empty arrays
            if (state.orderState.selectedDeliveryMethods.length === 0 && action.payload.length === 0) {
                return;
            }
            state.orderState.selectedDeliveryMethods = action.payload;
        },
        setOrderSelectedDocumentList(state, action: PayloadAction<Array<IDocListItem>>) {
            state.orderState.selectedDocumentList = action.payload;
        },
        setOrderCount(state, action: PayloadAction<number>) {
            state.orderState.orderCount = action.payload;
        },
        setOrderRequestTypeVisible(state, action: PayloadAction<boolean>) {
            state.orderState.requestTypeVisible = action.payload;
        },
        setOrderRequestTypeEnable(state, action: PayloadAction<boolean>) {
            state.orderState.requestTypeEnable = action.payload;
        },
        setOrderRequestTypeTitle(state, action: PayloadAction<string>) {
            state.orderState.requestTypeTitle = action.payload;
        },
        setOrderSolexEditorLink(state, action: PayloadAction<string | null>) {
            state.orderState.solexEditorLink = action.payload;
        },
        setConfigurationSaveFunction(state, action: PayloadAction<boolean>) {
            state.configurationSave.hasSaveFunction = action.payload;
        },
        setIsConfigurationPage(state, action: PayloadAction<boolean>) {
            state.configurationSave.isConfigurationPage = action.payload;
        }

    }
})

export const { setPage,
    setDefaultPackageId,
    setLoadingState,
    setConfigId,
    setConfigIds,
    setNeedSetupRedirect,
    setNeedHomeRedirect,
    setIsASO,
    addAttachments,
    clearAttachments,
    removeAttachment,
    initAppStateFromOrigin,
    setAuthToken,
    setClientCode,
    setServerUris,
    addRequestId,
    setSelectedPackage,
    setOrderPackageId,
    setOrderAltPackageId,
    setOrderParentPackageId,
    setOrderRequestType,
    setOrderSelectedDeliveryMethods,
    setOrderSelectedDocumentList,
    setOrderCount,
    setOrderRequestTypeVisible,
    setOrderRequestTypeEnable,
    setOrderRequestTypeTitle,
    setOrderSolexEditorLink,
    setConfigurationSaveFunction,
    setIsConfigurationPage
} = appSlice.actions;
export default appSlice.reducer